// Canvas painters for display lists.

import type { DrawItem, Layout, SystemBox } from './types';

export const MUSIC_FONT = 'Bravura';

let fontReady: Promise<void> | null = null;

/** Wait for the music font (ctx.fillText with Bravura needs it loaded). */
export function loadMusicFont(): Promise<void> {
  if (!fontReady) {
    fontReady = (async () => {
      if (typeof document === 'undefined' || !document.fonts) return;
      try {
        await document.fonts.load(`20px ${MUSIC_FONT}`, '');
        await document.fonts.ready;
      } catch {
        /* fall back to whatever is available */
      }
    })();
  }
  return fontReady;
}

export interface PaintOptions {
  /** Color override for every item (e.g. red for highlighted notes). */
  color?: string;
  /** Skip the white knock-out boxes behind tab numbers. */
  noKnockout?: boolean;
}

export function paintItem(ctx: CanvasRenderingContext2D, it: DrawItem, o: PaintOptions = {}): void {
  const color = o.color ?? ('color' in it ? it.color : undefined) ?? '#111';
  switch (it.k) {
    case 'line': {
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = it.w;
      ctx.lineCap = it.cap ?? 'butt';
      if (it.dash) ctx.setLineDash(it.dash);
      ctx.moveTo(it.x1, it.y1);
      ctx.lineTo(it.x2, it.y2);
      ctx.stroke();
      if (it.dash) ctx.setLineDash([]);
      break;
    }
    case 'glyph': {
      ctx.fillStyle = color;
      ctx.font = `${it.size}px ${MUSIC_FONT}`;
      ctx.textAlign = it.align ?? 'left';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(it.ch, it.x, it.y);
      break;
    }
    case 'text': {
      ctx.font = it.font;
      ctx.textAlign = it.align ?? 'left';
      ctx.textBaseline = it.baseline ?? 'alphabetic';
      if (it.bg && !o.noKnockout) {
        const m = ctx.measureText(it.text);
        const pad = it.bgPad ?? 2;
        const w = m.width + pad * 2;
        const h = (m.actualBoundingBoxAscent || 8) + (m.actualBoundingBoxDescent || 2) + pad * 2;
        const x = it.align === 'center' ? it.x - w / 2 : it.align === 'right' ? it.x - w : it.x - pad;
        const y = it.baseline === 'middle' ? it.y - h / 2 : it.y - h + pad;
        ctx.fillStyle = it.bg;
        ctx.fillRect(x, y, w, h);
      }
      ctx.fillStyle = color;
      ctx.fillText(it.text, it.x, it.y);
      break;
    }
    case 'rect': {
      if (it.fill) {
        ctx.fillStyle = o.color ?? it.fill;
        if (it.r) {
          ctx.beginPath();
          ctx.roundRect(it.x, it.y, it.w, it.h, it.r);
          ctx.fill();
        } else ctx.fillRect(it.x, it.y, it.w, it.h);
      }
      if (it.stroke) {
        ctx.strokeStyle = o.color ?? it.stroke;
        ctx.lineWidth = it.lw ?? 1;
        ctx.strokeRect(it.x, it.y, it.w, it.h);
      }
      break;
    }
    case 'poly': {
      ctx.beginPath();
      ctx.fillStyle = o.color ?? it.fill;
      ctx.moveTo(it.pts[0], it.pts[1]);
      for (let i = 2; i < it.pts.length; i += 2) ctx.lineTo(it.pts[i], it.pts[i + 1]);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'path': {
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = it.w;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      if (it.dash) ctx.setLineDash(it.dash);
      ctx.moveTo(it.pts[0], it.pts[1]);
      for (let i = 2; i < it.pts.length; i += 2) ctx.lineTo(it.pts[i], it.pts[i + 1]);
      ctx.stroke();
      if (it.dash) ctx.setLineDash([]);
      break;
    }
    case 'curve': {
      // Lens-shaped filled curve (ties, slurs): two beziers offset by the thickness.
      const t = it.thick;
      const dirY = it.cy1 < it.y1 ? -1 : 1;
      ctx.beginPath();
      ctx.fillStyle = color;
      ctx.moveTo(it.x1, it.y1);
      ctx.bezierCurveTo(it.cx1, it.cy1, it.cx2, it.cy2, it.x2, it.y2);
      ctx.bezierCurveTo(it.cx2, it.cy2 - dirY * t, it.cx1, it.cy1 - dirY * t, it.x1, it.y1);
      ctx.fill();
      ctx.lineWidth = Math.max(0.6, t * 0.35);
      ctx.strokeStyle = color;
      ctx.stroke();
      break;
    }
    case 'arc': {
      ctx.beginPath();
      ctx.arc(it.x, it.y, it.r, 0, Math.PI * 2);
      if (it.fill) {
        ctx.fillStyle = o.color ?? it.fill;
        ctx.fill();
      }
      if (it.stroke) {
        ctx.strokeStyle = o.color ?? it.stroke;
        ctx.lineWidth = it.lw ?? 1;
        ctx.stroke();
      }
      break;
    }
  }
}

export function paintItems(ctx: CanvasRenderingContext2D, items: readonly DrawItem[], o?: PaintOptions): void {
  for (const it of items) paintItem(ctx, it, o);
}

/** Paint a system into a context whose origin is layout (0,0). */
export function paintSystem(ctx: CanvasRenderingContext2D, sys: SystemBox): void {
  ctx.save();
  ctx.translate(0, sys.dy);
  paintItems(ctx, sys.items);
  ctx.restore();
}

/**
 * Paint every system intersecting [y0, y1] (layout coordinates). The context
 * must already be transformed so layout y0 maps to the top of the canvas.
 */
export function paintRange(ctx: CanvasRenderingContext2D, layout: Layout, y0: number, y1: number): void {
  if (layout.header.length && y0 < (layout.systems[0]?.y0 ?? Infinity)) paintItems(ctx, layout.header);
  for (const sys of layout.systems) {
    if (sys.y1 < y0 - 50 || sys.y0 > y1 + 50) continue;
    paintSystem(ctx, sys);
  }
}

/**
 * Offscreen per-system cache: re-blitting is much cheaper than repainting
 * glyphs. Keyed by the system's display list, which the layout engine reuses
 * for unchanged systems, so edits only repaint the systems they touch.
 */
export class SystemCache {
  private cache = new Map<DrawItem[], { canvas: HTMLCanvasElement | OffscreenCanvas; dpr: number; width: number; top: number; height: number }>();
  private used = new Set<DrawItem[]>();

  /** Returns a bitmap of the system and the local y of its top edge. */
  get(sys: SystemBox, width: number, dpr: number) {
    const hit = this.cache.get(sys.items);
    this.used.add(sys.items);
    if (hit && hit.dpr === dpr && hit.width === width) return hit;
    const pad = 40;
    const top = sys.y0 - sys.dy - pad;
    const height = sys.y1 - sys.y0 + pad * 2;
    const canvas: HTMLCanvasElement | OffscreenCanvas =
      typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(Math.ceil(width * dpr), Math.ceil(height * dpr)) : Object.assign(document.createElement('canvas'), { width: Math.ceil(width * dpr), height: Math.ceil(height * dpr) });
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    ctx.scale(dpr, dpr);
    ctx.translate(0, -top);
    paintItems(ctx, sys.items);
    const entry = { canvas, dpr, width, top, height };
    this.cache.set(sys.items, entry);
    return entry;
  }

  /** Forget bitmaps not used since the last sweep. */
  sweep(): void {
    for (const k of this.cache.keys()) if (!this.used.has(k)) this.cache.delete(k);
    this.used.clear();
  }

  clear(): void {
    this.cache.clear();
    this.used.clear();
  }
}
